import { removeDuplicates } from 'rcd-cpp-workspace/utils/array';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import * as React from 'react';
import { Metadata, SchemaField } from '../../../types';
import { DataMapping } from '../../../types/DataMapping';
import { ValidationResponse } from '../../../types/ValidationResponse';
import {
  clone,
  generateHashCode,
  getDefaultDataValue,
  getIntersection,
  getMetadata,
  getReferenceValue,
  getSchema
} from '../../../utils';
import {
  TreeFieldUtils,
  filterImplied,
  TreeSettings,
  Setting
} from '../../../utils/treeFieldUtils';
import { shouldFlattenImpliedNodes } from '../../../utils/treeUtils';
import { useSelector } from '../../../utils/useSelector';
import { ActionMenu } from '../../ActionMenu/actionMenu';
import { MenuAction } from '../../../types/MenuActions';
import { useImpliedDataConfirmModal } from '../../ConfirmModal/confirmModal';
import {
  Node,
  TreeSelect as TS,
  TreeSelectProps,
  traverse
} from '../../TreeSelect/treeSelect';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import { FieldProps } from '../metadataFormField';
import { ItemType } from '../../../enums/ItemType';
import { ThruCd } from '../../../enums/ThruCd';
import { AudienceMetadataField } from '../../../enums/AudienceMetadataField';
import { ContentType } from '../../../enums/ContentType';
import { useFullStory } from '../../App/fullStory';
import { FSEvent } from '../../../enums/FSEvent';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { AudienceDetailPrefix } from '../../../enums/AudienceDetailPrefix';
import './treeField.scss';

const MARKET_DEFINITION_NO_PRIMARY_FIELDS = [
  'additionalFunctions',
  'additionalRoles',
  'additionalQuestions'
];

export type TreeFieldProps = FieldProps &
  TreeSettings & {
    isDisabled?: (node: Node<Metadata>) => boolean;
    hideNodeMenu?: boolean;
  };

const TreeSelect = React.memo(
  ({
    vr,
    rerenderKey,
    ...p
  }: TreeSelectProps<Metadata, TreeSettings> & {
    vr: ValidationResponse;
    rerenderKey: string;
  }) => <TS {...p} noMappingMsg="No valid mappings" />,
  (prev, props) => {
    if (prev.isDisabled !== props.isDisabled) return false;
    if (!deepEquals(getRefIds(prev.selected), getRefIds(props.selected))) {
      return false;
    }
    if (!deepEquals(getRefIds(prev.implied), getRefIds(props.implied))) {
      return false;
    }
    if (!deepEquals(prev.vr, props.vr)) return false;
    if (prev.rerenderKey != props.rerenderKey) return false;
    return true;
  }
);

const getRefIds = (m: (string | Metadata)[]) =>
  m
    ?.map(s => {
      if (typeof s == 'string') return s;
      return (s as any).props.refId;
    })
    .sort();

export const TreeField = (props: TreeFieldProps) => {
  const {
    name,
    primarySettings,
    field,
    schema,
    data,
    disabled,
    updater,
    isDisabled: isNodeDisabled,
    hideNodeMenu
  } = props;
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const dataMapping = useSelector(s => s.planning.dataMapping);
  const workspace = useSelector(s => s.planning.selectedWorkspace);
  const ctConfigs = useSelector(s => s.planning.contentTypeConfigurations);
  const schemas = workspace?.schemas;
  const workspaceSetting = workspace?.setting;
  const { captureEvent } = useFullStory();

  if (!getAPI) return null;
  const { getTree } = getAPI();

  const settings = props.settings;

  const {
    getSelected,
    getFieldsToUpdate,
    getImpliedData,
    getUniqueKey,
    getChildren,
    isHidden,
    addToList,
    useSeeAll,
    renderData,
    getDemandQuestionPrefix,
    getTaxonomyTagForQuestion,
    removeFromList,
    flatten
  } = TreeFieldUtils;

  const { updateFields, update } = updater;
  const selected = getSelected(settings, data);

  const setSelected = (list: Metadata[]) => {
    updateFields(getFieldsToUpdate(list, settings, schema));
  };
  const { confirm, ConfirmModal } = useImpliedDataConfirmModal();

  const [topiccatTree, setTopiccatTree] = React.useState<Metadata[]>([]);
  const [isTopiccatTreeLoaded, setIsTopiccatTreeLoaded] = React.useState(false);
  
  React.useEffect(() => {
    if (field?.key === 'Item.primaryQuestion' || field?.key === 'Item.additionalQuestions') {
      setIsTopiccatTreeLoaded(false);
      getTree({ tree: 'topiccat' }, schemas).then(tree => {
        setTopiccatTree(tree || []);
        setIsTopiccatTreeLoaded(true);
      });
    } else {
      setIsTopiccatTreeLoaded(true);
    }
  }, [field?.key]);

  const { shouldHide, seeOptions, rerenderKey, hasNoResults } = React.useMemo(() => {
    return useSeeAll(
      workspaceSetting,
      settings,
      data,
      getMappedDataValue(field, data, dataMapping),
      field,
      ctConfigs
    );
  }, [workspaceSetting, settings, data, dataMapping, field, ctConfigs]);

  const noResultsMessages: Record<string, string> = {
    'Item.primaryQuestion':
      'There are no questions mapped to this Function/Role. Please select a different Function/Role or reach out to your Workspace Question Manager to add a new question.',
    'Item.rolePriority':
      'There are no Mission-Critical Priorities/Journey Steps mapped to this Function/Role. Please select a different Function/Role.',
    'Item.topicCategory':
      'There are no Perennial Priorities that correspond to your selected Function and Role.'
  };

  const noResultsMappingMsg =
    hasNoResults && field?.key ? noResultsMessages[field.key] || null : null;

  if (!settings?.length) return null;

  const derivedDataKeys = React.useMemo(
    () =>
      field.key != AudienceMetadataField.RolePriority
        ? new Set()
        : new Set(
            selected
              ?.filter(v => {
                if (
                  field.key == AudienceMetadataField.RolePriority &&
                  v?.relationship?.props?.thruCd == ThruCd.Derived
                ) {
                  return true;
                }
              })
              .map(v => getUniqueKey(v))
          ),
    [selected]
  );
  const isDisabled = React.useCallback(
    (node: Node<Metadata>) =>
      derivedDataKeys.has(node.key) ||
      (isNodeDisabled ? isNodeDisabled(node) : false),
    [derivedDataKeys, isNodeDisabled]
  );

  const makeRenderDataWithPrefix = (showPP: boolean) => (node: Node<Metadata, Setting>, onRemoveOrTxt?: any) => {
    const onRemove = typeof onRemoveOrTxt === 'function' ? onRemoveOrTxt : null;
    const baseText = renderData(node);
    if (!baseText) return null;
    if (!node.data?.props) return baseText;

    const isNonPriorityQuestion = node.parents?.some(p => p?.props?.refId === 'nonpriorityquestions');
    const taxonomyTag = isNonPriorityQuestion ? null : getTaxonomyTagForQuestion(node.data.props.refId, topiccatTree);
    const prefix = getDemandQuestionPrefix(node.data);

    const removeIcon = onRemove && node.isSelected ? (
      <Icon onClick={e => { e.stopPropagation(); onRemove(clone(node.data), clone(node.parents), node.setting, node.depth); }}>
        <Close />
      </Icon>
    ) : null;

    if (prefix) {
      if (showPP && taxonomyTag) {
        return (
          <span className="demand-question-label">
            <span className="demand-question-question">
              <span className="acronym-prefix">{prefix}</span>
              {baseText}
              {removeIcon}
            </span>
            <span className="demand-question-pp">
              <span className="acronym-prefix">{AudienceDetailPrefix.PerennialPriority}</span>
              ({taxonomyTag.props?.name || taxonomyTag.props?.description})
            </span>
          </span>
        );
      }
      return (
        <>
          <span className="acronym-prefix">{prefix}</span>
          {baseText}
          {removeIcon}
        </>
      );
    }
    if (removeIcon) {
      return <>{baseText}{removeIcon}</>;
    }
    return baseText;
  };
  const getTreeData = React.useCallback(async () => {
    return await getTree(props, schemas, field.key);
  }, [props, schemas, field?.key, getTree]);
  
  return (
    <FieldGroup
      {...props}
      settings={settings}
      field={{ ...field, name: name || field.name }}
      disabled={disabled && selected.length == 0}
      value={selected?.length ? true : false}
    >
      {(vr: ValidationResponse) => {
        return (
          <>
            <TreeSelect
              vr={vr}
              isDisabled={isDisabled}
              rerenderKey={rerenderKey}
              disabled={disabled}
              getData={getTreeData}
              onBlur={() => update()}
              settings={settings}
              selected={selected}
              implied={getImpliedData(props)}
              seeOptions={seeOptions}
              filterImplied={filterImplied(workspaceSetting, props, data)}
              flattenImplied={shouldFlattenImpliedNodes(settings)}
              renderNodeMenu={(node, implied) => {
                if (disabled || hideNodeMenu) return null;
                let accept: MenuAction, makePrimary: MenuAction;
                const getNewList = () => {
                  let newList: Metadata[] = [];
                  traverse([node], n => {
                    if (n.isLeaf && n.isSelected) {
                      newList = addToList(n.data, n.parents, newList);
                    } else if (n.isSelected && !n.numOfChildSelected) {
                      newList = addToList(n.data, n.parents, newList);
                    }
                  });
                  return newList;
                };
                if (implied && !field.multiValue) {
                  accept = ({ setLoading }) => {
                    const newList = getNewList();
                    if (newList.length) {
                      if (selected.length) {
                        confirm({
                          onConfirm: () => {
                            setLoading(true);
                            setSelected(newList);
                            update();
                          },
                          onCancel: () => setLoading(false)
                        });
                      } else {
                        setLoading(true);
                        setSelected(newList);
                        update();
                      }
                    }
                  };
                }
                if (primarySettings) {
                  const isMarketDefinitionItem =
                    getReferenceValue<number>(data, 'contentType', 'id') ==
                    ContentType.MARKET_DEFINITION;
                  const isHiddenField =
                    MARKET_DEFINITION_NO_PRIMARY_FIELDS.includes(field.field);
                  if (!isMarketDefinitionItem || !isHiddenField) {
                    makePrimary = ({ setLoading }) => {
                      const newList = getNewList();
                      const primaryValues = getSelected(
                        primarySettings.settings,
                        data
                      );
                      if (newList.length) {
                        let hasMultipleValues = false;
                        try {
                          const leafField =
                            settings[settings.length - 1]?.field?.field;
                          const leafCount = leafField
                            ? (data?.props?.[leafField]?.length ?? 0)
                            : selected.length;
                          hasMultipleValues = leafCount > 1;
                        } catch (e) {
                          console.error(
                            'An unexpected error occurred while calculating hasMultipleValues',
                            e
                          );
                        }
                        captureEvent(FSEvent.MakePrimaryClicked, {
                          fieldKey: field.key || '',
                          hasMultipleValues
                        });
                        const doUpdate = () => {
                          const refIds = newList.map(m => m?.props?.refId);
                          const secondaryValues = selected.filter(
                            m => !refIds.includes(m?.props?.refId)
                          );
                          const primaryFields = getFieldsToUpdate(
                            newList,
                            primarySettings.settings,
                            schema
                          );
                          const secondaryFields = getFieldsToUpdate(
                            primaryValues.concat(secondaryValues),
                            settings,
                            schema
                          );
                          updateFields([...primaryFields, ...secondaryFields]);
                          update().catch(() => {
                            setLoading(false);
                          });
                        };

                        if (primaryValues.length) {
                          confirm(
                            'The current primary audience will be set as an additional audience.',
                            {
                              header: 'Make Primary',
                              onConfirm: () => {
                                setLoading(true);
                                doUpdate();
                              },
                              onCancel: () => setLoading(false)
                            }
                          );
                        } else {
                          doUpdate();
                        }
                      }
                    };
                  }
                }
                if (accept || makePrimary) {
                  return (
                    <ActionMenu accept={accept} makePrimary={makePrimary} />
                  );
                }
              }}
              getClassName={(n, t) => {
                const cns = [];
                if ((t != 'display' && t != 'implied') || !vr) return cns;
                const refIds = vr.errors?.reduce<string[]>(
                  (ids, e) => ids.concat(e.refIds),
                  []
                );
                traverse([n], node => {
                  if (refIds?.includes(node.data?.props?.refId)) {
                    cns.push('--error');
                    return true;
                  }
                });
                return cns;
              }}
              isHidden={(data, parents, selected, ns) => {
                //if parent is included in default data,
                //check children of the parent to see if any children are included
                //if a child is included, exclude this data
                //TreeFieldUtils.getChildren and pass in parent
                if (selected[data.props.refId]) return false;
                if (
                  field?.field == 'contentType' &&
                  ['dtyp:184', 'dtyp:281', 'dtyp:185', 'dtyp:322'].includes(
                    data.props.refId
                  )
                ) {
                  return true;
                }
                if (shouldHide(data, parents, selected, ns)) return true;
                if (
                  field.parentField == 'backtag' &&
                  shouldHideBacktagData(data)
                )
                  return true;
                return isHidden(data);
              }}
              matches={(d, txt) =>
                (d?.props?.name || d?.props?.description || d?.props?.question)
                  ?.toLowerCase()
                  .indexOf(txt.toLowerCase()) >= 0
              }
              getUniqueKey={getUniqueKey}
              getChildren={getChildren}
              onChange={(d, parents, checked, depth) => {
                if (checked) {
                  setSelected(addToList(d, parents, selected));
                } else {
                  let remainingFields = removeFromList(d, selected);
                  for (let i = (depth ?? parents.length) - 1; i >= 0; i--) {
                    const parent = parents[i];
                    const parentKey = getUniqueKey(parent);
                    if (
                      !remainingFields.some(f => getUniqueKey(f) === parentKey)
                    )
                      continue;

                    const parentSetting = settings[i];
                    const isParentCheckboxHidden =
                      parentSetting?.hideCheckbox === true ||
                      (typeof parentSetting?.hideCheckbox === 'function' &&
                        (
                          parentSetting.hideCheckbox as (d: Metadata) => boolean
                        )(parent));
                    if (!isParentCheckboxHidden) continue;

                    const parentDescendantKeys = new Set(
                      flatten(getChildren(parent))
                    );
                    const hasRemainingChildren = remainingFields.some(f =>
                      parentDescendantKeys.has(getUniqueKey(f))
                    );
                    if (!hasRemainingChildren) {
                      remainingFields = removeFromList(parent, remainingFields);
                    }
                  }
                  setSelected(remainingFields);
                }
              }}
              renderData={field.dataType == 'DemandQuestion' ? makeRenderDataWithPrefix(false) : renderData}
              renderDisplayData={field.dataType == 'DemandQuestion' ? makeRenderDataWithPrefix(true) : undefined}
              highlightSearchText={true}
            >
              {noResultsMappingMsg && (
                <div className="no-mapping-msg">{noResultsMappingMsg}</div>
              )}
            </TreeSelect>

            <ConfirmModal />
          </>
        );
      }}
    </FieldGroup>
  );
};


const shouldHideBacktagData = (d: Metadata) => {
  if (d?.props) {
    if (!d.props.id) return true;
    let activeField = 'isActive';
    if (!d.props[activeField]) return true;
    if (d.metadata?.length) {
      for (var m of d.metadata) {
        if (!shouldHideBacktagData(m)) return false;
      }
      return true;
    }
  }
  return false;
};

const getMappedDataValue = (
  field: SchemaField,
  data?: Partial<Metadata>,
  mapping?: DataMapping
) => {
  const getRefIds = (list: Metadata[]) =>
    list?.map(l => l?.props?.refId).filter(x => x);

  if (!data?.props) return null;
  if (!mapping) return null;
  if (!field?.key) return null;
  const {
    fn2Roles,
    role2KIs,
    ki2Roles,
    role2Fn,
    role2ContentTypes,
    role2SubTypes,
    contentType2Roles,
    role2DemandQuestions,
    demandQuestion2Roles,
    role2TaxonomyTags,
    role2PriorityJourneySteps
  } = mapping;
  const isPrimary = [
    'Item.function',
    'Item.keyInitiative',
    'Item.primaryQuestion'
  ].includes(field.key);
  switch (field.key) {
    case 'Item.contentType':
      const roleRefIds = getRefIds(data.props.role) || [];
      const fnRefIds = getRefIds(data.props.function) || [];
      if (!roleRefIds.length && !fnRefIds.length) return null;
      const fnRoleRefIds = fnRefIds.reduce<string[]>(
        (acc, fnRefId) => {
          const roleIds = fn2Roles[fnRefId];
          if (roleIds?.length && !getIntersection(roleRefIds, roleIds).length) {
            return acc.concat(roleIds);
          }
          return acc;
        },
        [...roleRefIds]
      );
      const refIds = removeDuplicates(fnRoleRefIds).reduce((acc, roleId) => {
        const contentTypeRefIds = role2ContentTypes[roleId] || [];
        const subtypeRefIds = role2SubTypes[roleId] || [];
        if (contentTypeRefIds.length) acc.push(...contentTypeRefIds);
        if (subtypeRefIds.length) return acc.concat(subtypeRefIds);
        return acc;
      }, []);
      if (refIds.length) return removeDuplicates(refIds);
      else return [null];
    case 'Item.function':
    case 'Item.additionalFunctions': {
      const kiField = isPrimary ? 'keyInitiative' : 'additionalKeyInitiatives';
      const kiRefIds = getRefIds(data.props[kiField]) || [];
      let roleIds = kiRefIds.reduce((roleIds, kiRefId) => {
        const ids = ki2Roles[kiRefId];
        if (ids?.length) return roleIds.concat(ids);
        return roleIds;
      }, [] as string[]);

      const demandQuestionField = isPrimary
        ? 'primaryQuestion'
        : 'additionalQuestions';
      const demandQuestionRefIds =
        getRefIds(data.props[demandQuestionField]) || [];
      let demandQuestionRoleIds = demandQuestionRefIds.reduce(
        (roleIds, dqRefId) => {
          const ids = demandQuestion2Roles[dqRefId];
          if (ids?.length) return roleIds.concat(ids);
          return roleIds;
        },
        [] as string[]
      );
      if (demandQuestionRoleIds.length) {
        if (roleIds.length) {
          roleIds = getIntersection(roleIds, demandQuestionRoleIds);
        } else {
          roleIds = demandQuestionRoleIds;
        }
      }
      if (isPrimary && (!kiRefIds.length || !!roleIds.length)) {
        const contentTypeRefIds = getRefIds(data.props.contentType) || [];
        const subTypeRefIds = getRefIds(data.props.contentSubtype) || [];
        const csRefId = subTypeRefIds[0] || contentTypeRefIds[0];
        if (csRefId) {
          const csRoleIds = contentType2Roles[csRefId] || [];
          if (kiRefIds.length || demandQuestionRefIds.length) {
            roleIds = getIntersection(roleIds, csRoleIds);
          } else {
            roleIds = csRoleIds;
          }
        }
        if (
          !roleIds?.length &&
          (kiRefIds.length ||
            demandQuestionRefIds.length ||
            (isPrimary && contentTypeRefIds.length))
        ) {
          return [null];
        }
      }
      const fnIds = roleIds.map(roleId => role2Fn[roleId]).filter(x => x);
      if (roleIds?.length) return removeDuplicates([...roleIds, ...fnIds]);
      return null;
    }
    case 'Item.keyInitiative':
    case 'Item.additionalKeyInitiatives': {
      const roleField = isPrimary ? 'role' : 'additionalRoles';
      const fnField = isPrimary ? 'function' : 'additionalFunctions';
      const roleRefIds = getRefIds(data.props[roleField]) || [];
      const fnRefIds = getRefIds(data.props[fnField]) || [];
      if (!roleRefIds.length && !fnRefIds.length) return null;
      const fnRoleRefIds = fnRefIds.reduce<string[]>(
        (acc, fnRefId) => {
          const roleIds = fn2Roles[fnRefId];
          if (roleIds?.length && !getIntersection(roleRefIds, roleIds).length) {
            return acc.concat(roleIds);
          }
          return acc;
        },
        [...roleRefIds]
      );
      const kiRefIds = removeDuplicates(fnRoleRefIds).reduce((acc, roleId) => {
        const kiIds = role2KIs[roleId];
        if (kiIds?.length) return acc.concat(kiIds);
        return acc;
      }, []);
      if (kiRefIds.length) return removeDuplicates(kiRefIds);
      else if (roleRefIds.length) return [null];
      return null;
    }
    case 'Item.primaryQuestion':
    case 'Item.additionalQuestions': {
      const roleField = isPrimary ? 'role' : 'additionalRoles';
      const roleRefIds = getRefIds(data.props[roleField]) || [];
      if (!roleRefIds.length) return null;
      const demandQuestionRefIds = roleRefIds.reduce((acc, roleId) => {
        const dqRefIds = role2DemandQuestions[roleId] || [];
        return acc.concat(dqRefIds);
      }, [] as string[]);
      if (demandQuestionRefIds.length)
        return removeDuplicates(demandQuestionRefIds);
      return null;
    }
    case 'DemandQuestion.taxonomyTag': {
      const roleRefIds = getRefIds(data.props.role) || [];
      if (!roleRefIds.length) return null;
      let taxonomyTagRefIds = [];
      if (roleRefIds.length) {
        roleRefIds.forEach(roleId => {
          const ttRefIds = role2TaxonomyTags?.[roleId] || [];
          taxonomyTagRefIds = taxonomyTagRefIds.concat(ttRefIds);
        });
      }
      if (taxonomyTagRefIds.length) {
        return removeDuplicates(taxonomyTagRefIds);
      }
      return null;
    }
    case 'DemandQuestion.additionalTaxonomyTag': {
      const roleRefIds = getRefIds(data.props.role) || [];
      if (!roleRefIds.length) return null;

      // Get the currently selected primary taxonomy tags to exclude them
      const primaryTaxonomyTagRefIds = getRefIds(data.props.taxonomyTag) || [];

      let taxonomyTagRefIds = [];
      if (roleRefIds.length) {
        roleRefIds.forEach(roleId => {
          const ttRefIds = role2TaxonomyTags?.[roleId] || [];
          taxonomyTagRefIds = taxonomyTagRefIds.concat(ttRefIds);
        });
      }
      if (taxonomyTagRefIds.length) {
        // Filter out any taxonomy tags that are already selected as primary
        const filteredTaxonomyTagRefIds = removeDuplicates(
          taxonomyTagRefIds
        ).filter(refId => !primaryTaxonomyTagRefIds.includes(refId));
        return filteredTaxonomyTagRefIds.length
          ? filteredTaxonomyTagRefIds
          : null;
      }
      return null;
    }
    case 'DemandQuestion.priorityJourneyStep': {
      const roleRefIds = getRefIds(data.props.role) || [];
      if (!roleRefIds.length) return null;
      let priorityJourneyStepRefIds = [];
      if (roleRefIds.length) {
        roleRefIds.forEach(roleId => {
          const pjsRefIds = role2PriorityJourneySteps?.[roleId] || [];
          priorityJourneyStepRefIds =
            priorityJourneyStepRefIds.concat(pjsRefIds);
        });
      }
      if (priorityJourneyStepRefIds.length)
        return removeDuplicates(priorityJourneyStepRefIds);
      return null;
    }
    case 'Item.topicCategory': {
      if (data?.props?.itemType != ItemType.globalSubmission) {
        return null;
      }
      const roleRefIds = getRefIds(data.props.role) || [];
      const fnRefIds = getRefIds(data.props.function) || [];

      if (!roleRefIds.length) return null;

      const fnRoleRefIds = fnRefIds.reduce<string[]>(
        (acc, fnRefId) => {
          const roleIds = fn2Roles[fnRefId];
          if (roleIds?.length && !getIntersection(roleRefIds, roleIds).length) {
            return acc.concat(roleIds);
          }
          return acc;
        },
        [...roleRefIds]
      );

      let taxonomyTagRefIds = [];
      const allRoleRefIds = removeDuplicates(fnRoleRefIds);

      allRoleRefIds.forEach(roleId => {
        const ttRefIds = role2TaxonomyTags?.[roleId] || [];
        taxonomyTagRefIds = taxonomyTagRefIds.concat(ttRefIds);
      });

      const topicCategoryResult = taxonomyTagRefIds.length
        ? removeDuplicates(taxonomyTagRefIds)
        : [null];
      return topicCategoryResult;
    }
    case 'Item.rolePriority': {
      if (
        data?.props?.itemType != ItemType.globalSubmission &&
        data?.props?.itemType != ItemType.content
      ) {
        return null;
      }
      const roleRefIds = getRefIds(data.props.role) || [];
      const additionalRoleRefIds = getRefIds(data.props.additionalRoles) || [];
      const allRoleRefIds = [...roleRefIds, ...additionalRoleRefIds];
      if (!allRoleRefIds.length) return null;

      let priorityJourneyStepRefIds = [];
      allRoleRefIds.forEach(roleId => {
        const pjsRefIds = role2PriorityJourneySteps?.[roleId] || [];
        priorityJourneyStepRefIds = priorityJourneyStepRefIds.concat(pjsRefIds);
      });

      return priorityJourneyStepRefIds.length
        ? removeDuplicates(priorityJourneyStepRefIds)
        : [];
    }
  }
  return null;
};
